import { useRef, useCallback, useEffect, useState } from 'react'

import type { FormikProps } from 'formik'

import { useToast } from '@pnld/components-web'

import {
  useDashboardCitiesByState,
  useDashboardSchoolsByCity,
  useDashboardYearGrades,
} from '@/api/dashboard/queries'
import { getErrorMessage } from '@/utils/api'

import { GeoMapFilterValues, GeoMapFilterProps } from './types'

interface UseGeoMapFilterControllerProps {
  predictionYears?: GeoMapFilterProps['predictionYears']
  censusYears?: GeoMapFilterProps['censusYears']
  onChangeFilter?: (values: GeoMapFilterValues) => void
}

export const useGeoMapFilterController = (
  props?: UseGeoMapFilterControllerProps
) => {
  const toast = useToast()

  const { predictionYears, censusYears, onChangeFilter } = props || {}

  const initialValues: GeoMapFilterValues = {
    predictionYears: predictionYears?.isMulti ? [] : undefined,
    censusYears: censusYears?.isMulti ? [] : undefined,
    region: '',
    dependencies: [],
    states: [],
    locations: [],
    cities: [],
    spheres: [],
    schools: [],
    educationStages: [],
    hasPndlService: false,
    yearGrades: [],
  }

  const [formValues, setFormValues] =
    useState<GeoMapFilterValues>(initialValues)

  const formRef = useRef<FormikProps<GeoMapFilterValues> | null>(null)
  const previousFormValuesRef = useRef<GeoMapFilterValues>(initialValues)

  const selectRefs = useRef<Record<string, any>>({})

  const errorHandler = useCallback(
    (error: unknown) => {
      toast({
        status: 'error',
        title: getErrorMessage(error),
      })
    },
    [toast]
  )

  const handleOnSubmit = useCallback((_values: GeoMapFilterValues) => {
    // TODO: Deixei para apenas clicar no botão, mas se quiser auto fetch ao mudar algum filtro, é só chamar o onChangeFilter aqui
  }, [])

  useEffect(() => {
    const previous = previousFormValuesRef.current
    const current = formValues

    const formik = formRef.current

    if (!formik) {
      previousFormValuesRef.current = current
      return
    }

    const regionChanged = previous.region !== current.region
    if (regionChanged) {
      formik.setFieldValue('states', [])
      formik.setFieldValue('cities', [])
      formik.setFieldValue('schools', [])

      selectRefs.current.states?.clearValue?.()
      selectRefs.current.cities?.clearValue?.()
      selectRefs.current.schools?.clearValue?.()

      setFormValues(oldFormValues => ({
        ...oldFormValues,
        states: [],
        cities: [],
        schools: [],
      }))
    }

    const statesA = previous.states ?? []
    const statesB = current.states ?? []

    const stateChanged =
      statesA.length !== statesB.length ||
      statesA.some((value, index) => value !== statesB[index])

    if (stateChanged) {
      formik.setFieldValue('cities', [])
      formik.setFieldValue('schools', [])

      selectRefs.current.cities?.clearValue?.()
      selectRefs.current.schools?.clearValue?.()

      setFormValues(oldFormValues => ({
        ...oldFormValues,
        cities: [],
        schools: [],
      }))
    }

    const citiesA = previous.cities ?? []
    const citiesB = current.cities ?? []

    const cityChanged =
      citiesA.length !== citiesB.length ||
      citiesA.some((value, index) => value !== citiesB[index])

    if (cityChanged) {
      formik.setFieldValue('schools', [])

      selectRefs.current.schools?.clearValue?.()

      setFormValues(prev => ({
        ...prev,
        schools: [],
      }))
    }

    previousFormValuesRef.current = formik.values
  }, [formValues])

  const handleOnClear = useCallback(() => {
    formRef.current?.resetForm()
    setFormValues(initialValues)

    Object.values(selectRefs.current).forEach((ref: any) => {
      ref?.clearValue?.()
    })
  }, [initialValues])

  const handleOnFilter = useCallback(() => {
    if (formRef.current && onChangeFilter) {
      const values = formRef.current.values

      const hasPredictionYears =
        values.predictionYears &&
        ((Array.isArray(values.predictionYears) &&
          values.predictionYears.length > 0) ||
          (typeof values.predictionYears === 'string' &&
            values.predictionYears.trim() !== ''))

      const hasCensusYears =
        values.censusYears &&
        ((Array.isArray(values.censusYears) && values.censusYears.length > 0) ||
          (typeof values.censusYears === 'string' &&
            values.censusYears.trim() !== ''))

      if (
        Array.isArray(values.predictionYears) &&
        values.predictionYears.length > 5
      ) {
        toast({
          status: 'warning',
          title: 'Selecione no máximo 5 anos de predição.',
        })
        return
      }

      if (Array.isArray(values.censusYears) && values.censusYears.length > 5) {
        toast({
          status: 'warning',
          title: 'Selecione no máximo 5 anos de censo.',
        })
        return
      }

      const hasBothFields = predictionYears && censusYears

      if (hasBothFields) {
        if (!hasPredictionYears || !hasCensusYears) {
          const predictionText = predictionYears?.isMulti
            ? 'ao menos um ano de predição'
            : 'um ano de predição'
          const censusText = censusYears?.isMulti
            ? 'ao menos um ano de censo'
            : 'um ano de censo'

          toast({
            status: 'warning',
            title: `Ops! Por favor, selecione ${predictionText} e ${censusText}.`,
          })

          return
        }
      } else {
        if (!hasPredictionYears && !hasCensusYears) {
          const isMulti = predictionYears?.isMulti || censusYears?.isMulti
          const text = isMulti ? 'ao menos um ano' : 'um ano'

          toast({
            status: 'warning',
            title: `Ops! Por favor, selecione ${text}.`,
          })

          return
        }
      }

      onChangeFilter(values)
    }
  }, [onChangeFilter, toast, predictionYears, censusYears])

  const { data: cities, isLoading: isCitiesLoadingRaw } =
    useDashboardCitiesByState(
      {
        coUf: formValues.states,
      },
      {
        onError: errorHandler,
        enabled: !!formValues.states.length,
      }
    )

  const { data: schools, isLoading: isSchoolsLoadingRaw } =
    useDashboardSchoolsByCity(
      {
        coCity: formValues.cities,
      },
      {
        onError: errorHandler,
        enabled: !!formValues.cities.length,
      }
    )

  const isCitiesLoading = !!formValues.states.length && isCitiesLoadingRaw
  const isSchoolsLoading = !!formValues.cities.length && isSchoolsLoadingRaw

  const { data: yearGrades, isLoading: isYearGradesLoading } =
    useDashboardYearGrades({
      onError: errorHandler,
    })

  const currentValues = formRef.current?.values || formValues

  const isCitiesDisabled =
    !currentValues.states || currentValues.states.length === 0
  const isSchoolsDisabled =
    !currentValues.states ||
    currentValues.states.length === 0 ||
    !currentValues.cities ||
    currentValues.cities.length === 0

  const isHasPnldStagesSelected = currentValues.hasPndlService
  const isEducationStagesSelected =
    currentValues.educationStages && currentValues.educationStages.length > 0
  const isYearGradesSelected =
    currentValues.yearGrades && currentValues.yearGrades.length > 0

  const isHasPnldStagesDisabled =
    isEducationStagesSelected || isYearGradesSelected
  const isEducationStagesDisabled =
    isHasPnldStagesSelected || isYearGradesSelected
  const isYearGradesDisabled =
    isHasPnldStagesSelected || isEducationStagesSelected

  return {
    cities: {
      value: cities,
      isLoading: isCitiesLoading,
    },
    schools: {
      value: schools,
      isLoading: isSchoolsLoading,
    },
    yearGrades: {
      value: yearGrades,
      isLoading: isYearGradesLoading,
    },
    formRef,
    formValues,
    initialValues,
    selectRefs,
    handleOnSubmit,
    handleOnClear,
    handleOnFilter,
    syncFormValues: setFormValues,
    isCitiesDisabled,
    isSchoolsDisabled,
    isHasPnldStagesDisabled,
    isEducationStagesDisabled,
    isYearGradesDisabled,
  }
}
